import { Transition } from '@headlessui/react';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import PropTypes from 'prop-types';
import { use, useCallback, useEffect, useMemo, useState } from 'react';
import { useDrop } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Outlet, UNSAFE_DataRouterContext } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import useLocalStorageState from 'use-local-storage-state';

import { getEnv } from '../lib/env';
import { putUserConfig } from '../lib/flotiq-client';
import { checkResponseStatus } from '../lib/flotiq-client/response-errors';
import FlotiqPlugins from '../lib/flotiq-plugins/flotiqPluginsRegistry';
import { LanguageChangedEvent } from '../lib/flotiq-plugins/plugin-events/LanguageChangedEvent';
import { getTestProps } from '../lib/helpers';

import ErrorFallback from '../components/ErrorFallback/ErrorFallback';
import GlobalSearch from '../components/GlobalSearch/GlobalSearch';

import { useSpaceMenuItems } from '../hooks/UIPersonalization/useSpaceMenuItems';
import { usePluginsSettings } from '../hooks/api';
import { useAllSpaceConstraints } from '../hooks/api/useAllSpaceConstraints';
import useMediaUpload from '../hooks/api/useMediaUpload';
import useOnce from '../hooks/useOnce';
import useSelectedSpace from '../hooks/useSelectedSpace';
import useToken from '../hooks/useToken';

import { useModals } from '../contexts/ModalContext';

import { UnionIcon } from '../images/shapes';

import LoaderPage from '../pages/LoaderPage/LoaderPage';
import PluginsExceededModalContent from './PluginsExceededModalContent/PluginsExceededModalContent';
import Structure from './Structure';

const USER_PLUGINS_PARAMS = {
  limit: 1000,
  page: 1,
};

/**
 * @emits FlotiqPlugins."flotiq.language::changed"
 */
const Layout = ({ testId = '', additionalClasses = '', children }) => {
  const { t, i18n } = useTranslation();
  const { space, buildUrlWithSpace, spaceLimits } = useSelectedSpace();
  const [user, setUser] = useLocalStorageState('cms.user');
  const [layoutLoading, setLayoutLoading] = useState(FlotiqPlugins.enabled());
  const [pluginsLoaded, setPluginLoaded] = useState(false);
  const jwt = useToken();
  const modal = useModals();

  const queryClient = useQueryClient();

  const { allConstraints, isLoading: allConstraintsLoading } =
    useAllSpaceConstraints(space);

  const dataRouterContext = use(UNSAFE_DataRouterContext);

  const updateUserMimeTypes = useCallback(async () => {
    const allowedMimetypes = allConstraints?.mimetypes;
    if (allowedMimetypes && user) {
      setUser({
        ...user,
        data: { ...user.data, allowedMimetypes },
      });
    }
  }, [allConstraints?.mimetypes, setUser, user]);

  useOnce(updateUserMimeTypes);

  const systemPlugins = useMemo(
    () => JSON.parse(getEnv('REACT_APP_SYSTEM_PLUGINS') || '[]'),
    [],
  );

  const officialPluginsLimitExceeded =
    spaceLimits?.official_plugins_limit > -1 &&
    spaceLimits?.official_plugins_limit <
      allConstraints?.['official-plugins-count'];

  const customPluginsLimitExceeded =
    spaceLimits?.custom_plugins_limit > -1 &&
    spaceLimits?.custom_plugins_limit <
      allConstraints?.['custom-plugins-count'];

  const pluginsExceeded =
    spaceLimits?.price > -1 &&
    (officialPluginsLimitExceeded || customPluginsLimitExceeded);

  const showPluginsExceededModal = useCallback(
    (limit, count) =>
      modal({
        id: 'plugins-exceeded',
        content: (
          <PluginsExceededModalContent
            limit={limit}
            count={count}
            {...getTestProps(testId, 'plugins-exceeded', 'testId')}
          />
        ),
        size: 'xl',
        buttons: [
          {
            key: 'upgrade',
            color: 'blue',
            link: `/space/upgrade/${space}`,
            label: t('Limits.PluginsExceeded.UpgradeYourPlan'),
          },
          {
            key: 'manage',
            color: 'blueBordered',
            link: buildUrlWithSpace('plugins'),
            label: t('Limits.PluginsExceeded.ManagePlugins'),
          },
        ],
      }),
    [buildUrlWithSpace, modal, space, t, testId],
  );

  useEffect(() => {
    if (allConstraintsLoading || !pluginsExceeded) return;

    showPluginsExceededModal(
      officialPluginsLimitExceeded
        ? spaceLimits?.official_plugins_limit
        : spaceLimits?.custom_plugins_limit,
      allConstraints?.[
        officialPluginsLimitExceeded
          ? 'official-plugins-count'
          : 'custom-plugins-count'
      ],
    );
  }, [
    allConstraints,
    allConstraintsLoading,
    officialPluginsLimitExceeded,
    spaceLimits?.custom_plugins_limit,
    spaceLimits?.official_plugins_limit,
    pluginsExceeded,
    showPluginsExceededModal,
  ]);

  const { data: userPlugins, isFetching: userPluginsAreLoading } =
    usePluginsSettings(USER_PLUGINS_PARAMS);

  useEffect(() => {
    setLayoutLoading(true);
    setPluginLoaded(false);
  }, [space]);

  useEffect(() => {
    if (pluginsLoaded || userPluginsAreLoading || allConstraintsLoading) return;

    FlotiqPlugins.spaceId = space;
    FlotiqPlugins.router = dataRouterContext.router;
    FlotiqPlugins.queryClient = queryClient;

    const enabledPlugins = pluginsExceeded
      ? []
      : userPlugins.filter(({ enabled }) => enabled);

    Promise.all(
      [...systemPlugins, ...enabledPlugins].map(({ id, url, settings }) => {
        if (!id || !url) return null;
        return FlotiqPlugins.loadPlugin(id, url, settings);
      }),
    ).then(() => {
      setLayoutLoading(false);
      setPluginLoaded(true);
    });
  }, [
    allConstraintsLoading,
    dataRouterContext.router,
    pluginsExceeded,
    pluginsLoaded,
    queryClient,
    space,
    systemPlugins,
    userPlugins,
    userPluginsAreLoading,
  ]);

  const { onUpload } = useMediaUpload();

  const pinContentCallback = useCallback(
    async (id, pined) => {
      if (!user?.data) return;

      const removeElement = (arr, id) => {
        const index = arr[space].indexOf(id);
        if (index > -1) {
          arr[space].splice(index, 1);
        }
        return arr;
      };

      let newPinnedContent = { ...user.data.config?.pinedDefinitions };

      if (!pined) {
        if (!newPinnedContent[space]) {
          newPinnedContent[space] = [id];
        } else {
          newPinnedContent[space].push(id);
        }
      } else {
        newPinnedContent = removeElement(newPinnedContent, id);
      }

      const newConfig = {
        ...user.data.config,
        pinedDefinitions: newPinnedContent,
      };

      try {
        const { body, status } = await putUserConfig(jwt, null, {
          id: user.data.id,
          config: newConfig,
        });
        checkResponseStatus(body, status);

        const newUser = { ...user };
        newUser.data.config = newConfig;

        setUser(newUser);
      } catch {
        toast.error(t('Form.CommunicationErrorMessage'));
      }
    },
    [jwt, setUser, space, t, user],
  );

  const { menuItems: sidebarMenuItems } = useSpaceMenuItems();

  useEffect(() => {
    const lngChangedCallback = (language) => {
      FlotiqPlugins.run(
        'flotiq.language::changed',
        new LanguageChangedEvent({ language }),
      );
      moment.locale(language === 'en' ? 'en-gb' : 'pl');
    };

    i18n.on('languageChanged', lngChangedCallback);
    return () => i18n.off('languageChanged', lngChangedCallback);
  }, [i18n]);

  const [{ canDrop, isOver }, drop] = useDrop(
    () => ({
      accept: [NativeTypes.FILE],
      drop(item) {
        onUpload(item.files);
      },
      canDrop: () => true,
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [onUpload],
  );

  const getErrorFallbackPage = (event) => <ErrorFallback {...event} />;

  return layoutLoading ? (
    <LoaderPage />
  ) : (
    <Sentry.ErrorBoundary fallback={getErrorFallbackPage}>
      <GlobalSearch />
      <Structure
        testId={testId}
        showUpgrade
        additionalClasses={additionalClasses}
        ref={() => drop(document.body)}
        logoLink={space ? buildUrlWithSpace('') : '/'}
        sidebarMenuItems={sidebarMenuItems}
        pinContentCallback={pinContentCallback}
        additionalElement={
          <Transition show={canDrop && isOver}>
            <div className="z-50 ease-in duration-200 data-[closed]:opacity-0">
              <div className="fixed top-0 right-0 h-screen w-full bg-white opacity-75 z-50" />
              <div
                className={twMerge(
                  'fixed top-1/2 left-1/2 bg-blue aspect-square rounded-full z-50',
                  'h-60 md:h-80 lg:h-96 2xl:h-[500px]',
                  'text-white font-bold text-center',
                  'text-2xl md:text-3xl lg:text-4xl 2xl:text-5xl -translate-x-1/2 -translate-y-1/2',
                )}
                {...getTestProps(testId, 'add-media')}
              >
                <div className="absolute h-full w-full animate-slowPing rounded-full bg-blue opacity-20" />
                <div
                  className={
                    'h-full w-full p-5 md:p-8 lg:py-10 2xl:py-20 grid grid-rows-3 justify-center items-center'
                  }
                >
                  <UnionIcon className="animate-slowSpin justify-self-center h-2 md:h-4 lg:h-6 opacity-80" />
                  <p>{t('Media.DropFiles')}</p>
                  <p className="px-5 2xl:px-20 self-end opacity-80 text-base md:text-2xl 2xl:text-3xl">
                    {t('Media.AddToLibrary')}
                  </p>
                </div>
              </div>
            </div>
          </Transition>
        }
      >
        {children ? children : <Outlet />}
      </Structure>
    </Sentry.ErrorBoundary>
  );
};

export default Layout;

Layout.propTypes = {
  /*
   ** Checking if the sidebar is open
   */
  isSidebarOpen: PropTypes.bool,
  /*
   ** Additional classes for layout
   */
  additionalClasses: PropTypes.string,
  /**
   * Test id for layout
   */
  testId: PropTypes.string,
};
